// Operator › Supervisor load (`/operator/supervisors?p=`; M5-05, 06 §15; Reports / Ops UX pass
// 2026-10-21): each person who supervises digital employees across the operator's companies,
// one row per person, with their DEs, supervision time, waiting approvals and open reviews, and
// each company's share on demand (`GET /v1/operator/supervisor-load`, one call). The 56 px
// location header, a 40 px toolbar (week), four compact tiles, the unsupervised DEs as one
// notice, the table with Open DE on a company row.
import {
  Badge,
  Button,
  EmptyState,
  LocationHeader,
  Notice,
  StatTile,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Toolbar,
  WorkerBadge,
  workerName,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  BotIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  HourglassIcon,
  TimerIcon,
  TriangleAlertIcon,
  UsersIcon,
} from "lucide-react";
import { Fragment, useState } from "react";
import type { Api } from "../../api/client";
import {
  OpsBody,
  OpsFrame,
  operatorCrumb,
  RowActions,
  RowLinkAction,
  RowSub,
  TABLE_ROW_HOVER,
  Tiles,
} from "../ops-ux";
import { LoadError, Loading } from "../screen-parts";
import { CoverageNotice, WeekSelect } from "./agency-parts";
import { ageText, hMm, type SupervisorLoad, supervisorLoadQuery } from "./agency-queries";

const REASON: Record<SupervisorLoad["unsupervised"][number]["reason"], string> = {
  no_supervisor_seat: "its seat has no supervisor seat",
  supervisor_seat_unfilled: "nobody holds its supervisor seat",
};

export function SupervisorLoadPage({
  api,
  week,
  onWeek,
}: {
  api: Api;
  week: string;
  onWeek: (week: string) => void;
}) {
  const q = useQuery(supervisorLoadQuery(api, week));
  return (
    <OpsFrame>
      <LocationHeader
        crumbs={[operatorCrumb, { label: "Supervisor load" }]}
        icon={UsersIcon}
        title="Supervisor load"
        count={q.data ? q.data.people.length : undefined}
      />
      <Toolbar bleed>
        <WeekSelect week={week} onWeek={onWeek} />
        <span className="ml-auto shrink-0 text-xs text-muted-foreground">
          Supervision is approved time in h:mm.
        </span>
      </Toolbar>
      <OpsBody>
        {q.isPending ? (
          <Loading label="Loading supervisor load" rows={5} />
        ) : q.isError ? (
          <LoadError what="Supervisor load" error={q.error} />
        ) : (
          <LoadBody data={q.data} week={week} />
        )}
      </OpsBody>
    </OpsFrame>
  );
}

function LoadBody({ data, week }: { data: SupervisorLoad; week: string }) {
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (key: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const des = data.people.reduce((a, p) => a + p.de_count, 0);
  const minutes = data.people.reduce((a, p) => a + p.supervision_minutes, 0);
  const pending = data.people.reduce((a, p) => a + p.pending_approvals, 0);
  return (
    <>
      <CoverageNotice complete={data.complete} reasons={data.incomplete_reasons} />
      <Tiles data-slot="load-tiles" aria-label="Supervisor load totals">
        <StatTile
          size="sm"
          label="Supervisors"
          icon={UsersIcon}
          value={data.people.length}
          detail={`${des} ${des === 1 ? "DE" : "DEs"} supervised`}
        />
        <StatTile
          size="sm"
          label="Supervision"
          icon={TimerIcon}
          value={hMm(minutes)}
          detail={`h:mm, week ${week}`}
        />
        <StatTile
          size="sm"
          label="Pending approvals"
          icon={HourglassIcon}
          value={pending}
          detail="on supervisors' seats, now"
        />
        <StatTile
          size="sm"
          label="Unsupervised DEs"
          icon={TriangleAlertIcon}
          value={data.unsupervised.length}
          detail={data.unsupervised.length ? "need a supervisor seat" : "every DE has one"}
        />
      </Tiles>
      {data.unsupervised.length > 0 ? (
        <Notice
          tone="warning"
          title={`${data.unsupervised.length} unsupervised ${data.unsupervised.length === 1 ? "digital employee" : "digital employees"}`}
          data-slot="unsupervised"
        >
          <ul className="mt-1 flex flex-col gap-1">
            {data.unsupervised.map((u) => (
              <li key={u.de.membership_id}>
                <WorkerBadge kind="de" size="sm" className="mr-1 align-middle" />
                <Link
                  to="/c/$slug/agents/de/$id"
                  params={{ slug: u.company.slug, id: u.de.membership_id }}
                  search={{ p: week }}
                  className="font-medium underline-offset-4 hover:underline"
                >
                  {workerName(u.de.display_name)}
                </Link>{" "}
                · {u.company.name}: {REASON[u.reason]}.
              </li>
            ))}
          </ul>
        </Notice>
      ) : null}
      {data.people.length === 0 ? (
        <EmptyState
          variant="card"
          icon={UsersIcon}
          title="Nobody supervises a digital employee yet"
          description="People holding a DE's supervisor seat appear here with their load."
        />
      ) : (
        <Table data-slot="supervisor-load">
          <TableCaption>One row per person; open a row for each company.</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead scope="col">Supervisor</TableHead>
              <TableHead scope="col" className="text-right">
                DEs
              </TableHead>
              <TableHead scope="col" className="text-right">
                Supervision
              </TableHead>
              <TableHead scope="col" className="text-right">
                Pending approvals
              </TableHead>
              <TableHead scope="col" className="text-right">
                Oldest waiting
              </TableHead>
              <TableHead scope="col" className="text-right">
                Open reviews
              </TableHead>
              <TableHead scope="col" className="text-right">
                Companies
              </TableHead>
              <TableHead scope="col">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.people.map((p) => {
              const expanded = open.has(p.key);
              return (
                <Fragment key={p.key}>
                  <TableRow data-person={p.key}>
                    <TableHead scope="row" className="font-normal">
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-expanded={expanded}
                        aria-label={`${expanded ? "Hide" : "Show"} companies of ${p.display_name}`}
                        onClick={() => toggle(p.key)}
                        className="-ml-2"
                      >
                        {expanded ? (
                          <ChevronDownIcon aria-hidden="true" />
                        ) : (
                          <ChevronRightIcon aria-hidden="true" />
                        )}
                        <span className="font-medium text-foreground">{p.display_name}</span>
                      </Button>
                    </TableHead>
                    <TableCell className="text-right tabular-nums">{p.de_count}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {hMm(p.supervision_minutes)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {p.pending_approvals > 0 ? (
                        <Badge variant="info">{p.pending_approvals}</Badge>
                      ) : (
                        0
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {ageText(p.oldest_pending_minutes)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{p.open_reviews}</TableCell>
                    <TableCell className="text-right tabular-nums">{p.companies.length}</TableCell>
                    <TableCell />
                  </TableRow>
                  {expanded
                    ? p.companies.map((c) => (
                        <TableRow
                          key={`${p.key}:${c.company.id}`}
                          className={`bg-sunken ${TABLE_ROW_HOVER}`}
                          data-person-company={c.company.id}
                        >
                          <TableCell className="pl-10">
                            <div className="font-medium">{c.company.name}</div>
                            <RowSub>
                              {c.seats.map((s) => s.title).join(", ")} ·{" "}
                              {c.des.length
                                ? c.des.map((d, i) => (
                                    <span
                                      key={d.membership_id}
                                      className="inline-flex items-center gap-1"
                                    >
                                      <WorkerBadge kind="de" size="sm" />
                                      {workerName(d.display_name)}
                                      {i < c.des.length - 1 ? "," : ""}
                                    </span>
                                  ))
                                : "no DEs"}
                            </RowSub>
                          </TableCell>
                          <TableCell className="text-right tabular-nums">{c.de_count}</TableCell>
                          <TableCell className="text-right tabular-nums">
                            {hMm(c.supervision_minutes)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {c.pending_approvals}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {ageText(c.oldest_pending_minutes)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {c.open_reviews}
                          </TableCell>
                          <TableCell className="text-right text-xs text-muted-foreground">
                            {c.week_key}
                          </TableCell>
                          <TableCell className="text-right">
                            <RowActions>
                              {c.des.map((d) => (
                                <RowLinkAction
                                  key={d.membership_id}
                                  icon={BotIcon}
                                  label={`Open ${workerName(d.display_name)}`}
                                  render={
                                    <Link
                                      to="/c/$slug/agents/de/$id"
                                      params={{ slug: c.company.slug, id: d.membership_id }}
                                      search={{ p: week }}
                                    />
                                  }
                                />
                              ))}
                            </RowActions>
                          </TableCell>
                        </TableRow>
                      ))
                    : null}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      )}
    </>
  );
}
